import type { AnyCircuitElement, PcbPlatedHole } from "circuit-json"
import { pairs } from "../utils/pairs"
import { gerberBuilder } from "../gerber-builder"
import type { LayerToGerberCommandsMap } from "./GerberLayerName"
import { defineCommonMacros } from "./define-common-macros"
import {
  defineAperturesForLayer,
  REGION_APERTURE_CONFIG,
  getApertureConfigFromCirclePcbHole,
  getApertureConfigFromPcbPlatedHole,
  getApertureConfigFromPcbPlatedHoleSoldermask,
  getApertureConfigFromCirclePcbHoleSoldermask,
  getApertureConfigFromPcbCopperText,
  getApertureConfigFromPcbFabricationNotePath,
  getApertureConfigFromPcbFabricationNoteText,
  getApertureConfigFromPcbSilkscreenPath,
  getApertureConfigFromPcbSilkscreenText,
  getApertureConfigFromPcbSmtpad,
  getApertureConfigFromPcbSmtpadSoldermask,
  getApertureConfigFromPcbSolderPaste,
  getApertureConfigFromOuterDiameter,
} from "./defineAperturesForLayer"
import type { PcbCutout } from "circuit-json"
import { findApertureNumber } from "./findApertureNumber"
import { getCommandHeaders } from "./getCommandHeaders"
import { getGerberLayerName } from "./getGerberLayerName"
import { offsetPolygonOutline } from "./offsetPolygonOutline"
import { lineAlphabet } from "@tscircuit/alphabet"
import {
  applyToPoint,
  compose,
  identity,
  rotate,
  translate,
  type Matrix,
} from "transformation-matrix"
import type { AnyGerberCommand } from "../any_gerber_command"
import type { LayerRef } from "circuit-json"
import {
  getFabricationLayerRefs,
  isOuterLayerRef,
  outerLayerRefs,
} from "./fabricationLayerRefs"
import { getFabRectPoints } from "./getFabRectPoints"
import { renderFabricationDimension } from "./renderFabricationDimension"
import { renderOpenPath } from "./renderOpenPath"
import {
  getSilkscreenShapeStroke,
  isSilkscreenShape,
} from "./getSilkscreenShapeStroke"
import polygonClipping, {
  type MultiPolygon,
  type Polygon,
  type Ring,
} from "polygon-clipping"
import {
  doesSolidCutoutOverlapBoardEdge,
  getBoardOutlinePolygons,
  isCutoutFullyInternal,
  getSolidCutoutOutlinePolygon,
} from "../utils/boardCutoutGeometry"
import { emitCutoutEdgeCuts } from "../utils/emitCutoutEdgeCuts"

type Point = { x: number; y: number }

const closeRing = (ring: Ring): Ring => {
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (!first || !last) return ring
  if (first[0] === last[0] && first[1] === last[1]) return ring
  return [...ring, first]
}

const emitEdgeCutRing = (
  gerberBuild: ReturnType<typeof gerberBuilder>,
  ring: Ring,
  mfy: (y: number) => number,
) => {
  const closedRing = closeRing(ring)
  const firstPoint = closedRing[0]
  if (!firstPoint || closedRing.length < 4) return

  gerberBuild.add("move_operation", {
    x: firstPoint[0],
    y: mfy(firstPoint[1]),
  })

  for (let i = 1; i < closedRing.length; i++) {
    const point = closedRing[i]
    gerberBuild.add("plot_operation", {
      x: point[0],
      y: mfy(point[1]),
    })
  }
}

const getLayerCount = (circuitJson: AnyCircuitElement[]) => {
  const board = circuitJson.find((element) => element.type === "pcb_board") as
    | { num_layers?: number }
    | undefined
  const numLayers = board?.num_layers ?? 2
  return Math.max(2, Math.min(10, numLayers))
}

const getInnerLayerRefs = (layerCount: number): LayerRef[] =>
  Array.from({ length: layerCount - 2 }, (_, i) => `inner${i + 1}` as LayerRef)

const getGerberInnerLayerName = (layerRef: LayerRef) => {
  if (!layerRef.startsWith("inner")) {
    throw new Error(`Expected inner layer, got ${layerRef}`)
  }
  return `In${layerRef.replace("inner", "")}_Cu` as const
}

/**
 * Converts Circuit JSON to arrays of Gerber commands for each layer
 */
export const convertSoupToGerberCommands = (
  circuitJson: AnyCircuitElement[],
  opts: { flip_y_axis?: boolean } = {},
): LayerToGerberCommandsMap => {
  opts.flip_y_axis ??= false
  const hasPanel = circuitJson.some((e) => e.type === "pcb_panel")
  const hasBoard = circuitJson.some((e) => e.type === "pcb_board")
  const layerCount = getLayerCount(circuitJson)
  const innerLayerRefs = getInnerLayerRefs(layerCount)
  const copperLayerRefs = ["top", ...innerLayerRefs, "bottom"] as LayerRef[]
  const fabricationLayerRefs = getFabricationLayerRefs(circuitJson)
  const glayers: LayerToGerberCommandsMap = {
    F_Cu: getCommandHeaders({
      layer: "top",
      layer_type: "copper",
      total_layer_count: layerCount,
    }),
    F_SilkScreen: getCommandHeaders({
      layer: "top",
      layer_type: "silkscreen",
    }),
    F_Mask: getCommandHeaders({
      layer: "top",
      layer_type: "soldermask",
    }),
    F_Paste: getCommandHeaders({
      layer: "top",
      layer_type: "paste",
    }),
    B_Cu: getCommandHeaders({
      layer: "bottom",
      layer_type: "copper",
      total_layer_count: layerCount,
    }),
    B_SilkScreen: getCommandHeaders({
      layer: "bottom",
      layer_type: "silkscreen",
    }),
    B_Mask: getCommandHeaders({
      layer: "bottom",
      layer_type: "soldermask",
    }),
    B_Paste: getCommandHeaders({
      layer: "bottom",
      layer_type: "paste",
    }),
    Edge_Cuts: getCommandHeaders({
      layer: "edgecut",
    }),
  }

  for (const innerLayerRef of innerLayerRefs) {
    glayers[getGerberInnerLayerName(innerLayerRef)] = getCommandHeaders({
      layer: innerLayerRef,
      layer_type: "copper",
      total_layer_count: layerCount,
    })
  }

  for (const layerRef of fabricationLayerRefs) {
    glayers[getGerberLayerName(layerRef, "fabrication")] = getCommandHeaders({
      layer: layerRef,
      layer_type: "fabrication",
    })
  }

  const copperGerberLayerNames = copperLayerRefs.map((layerRef) =>
    getGerberLayerName(layerRef, "copper"),
  )
  const fabricationGerberLayerNames = fabricationLayerRefs.map((layerRef) =>
    getGerberLayerName(layerRef, "fabrication"),
  )
  const outerGerberLayerNames = outerLayerRefs.flatMap((layerRef) => [
    getGerberLayerName(layerRef, "soldermask"),
    getGerberLayerName(layerRef, "paste"),
    getGerberLayerName(layerRef, "silkscreen"),
  ])

  for (const glayer_name of [
    "F_Cu",
    ...copperGerberLayerNames.filter((name) => name !== "F_Cu"),
    ...fabricationGerberLayerNames,
    ...outerGerberLayerNames,
  ] as const) {
    const glayer = glayers[glayer_name]
    defineCommonMacros(glayer)
    defineAperturesForLayer({
      circuitJson,
      glayer,
      glayer_name,
    })
  }

  // Edgecuts has a single aperature
  glayers.Edge_Cuts.push(
    ...gerberBuilder()
      .add("define_aperture_template", {
        aperture_number: 10,
        standard_template_code: "C",
        diameter: 0.05, //mm
      })
      .build(),
  )

  /**
   * "maybe flip y axis" to handle y axis negating
   */
  const mfy = (y: number) => {
    if (opts.flip_y_axis) {
      return -y
    }
    return y
  }
  const boardOutlinePolygons = getBoardOutlinePolygons(circuitJson)

  // Only cutouts that straddle the board edge are subtracted from the outline.
  // Internal cutouts stay as standalone CW Edge.Cuts loops (see pcb_cutout branch).
  const solidCutoutPolygons = circuitJson
    .filter((element): element is PcbCutout => element.type === "pcb_cutout")
    .filter((cutout) =>
      doesSolidCutoutOverlapBoardEdge({ cutout, boardOutlinePolygons }),
    )
    .map(getSolidCutoutOutlinePolygon)
    .filter((polygon): polygon is Polygon => polygon !== null)

  const renderPillFlash = ({
    glayer,
    x,
    y,
    width,
    height,
    rotationDegrees = 0,
  }: {
    glayer: AnyGerberCommand[]
    x: number
    y: number
    width: number
    height: number
    rotationDegrees?: number
  }) => {
    const circleApertureConfig = {
      standard_template_code: "C" as const,
      diameter: Math.min(width, height),
    }
    const apertureNumber = findApertureNumber(glayer, circleApertureConfig)
    const gb = gerberBuilder().add("select_aperture", {
      aperture_number: apertureNumber,
    })

    const offset = Math.abs(width - height) / 2
    const rotationRadians = (rotationDegrees * Math.PI) / 180
    const cosTheta = Math.cos(rotationRadians)
    const sinTheta = Math.sin(rotationRadians)

    const rotateAndTranslate = (dx: number, dy: number) => ({
      x: x + dx * cosTheta - dy * sinTheta,
      y: y + dx * sinTheta + dy * cosTheta,
    })

    if (offset <= 1e-9) {
      gb.add("flash_operation", { x, y: mfy(y) })
      glayer.push(...gb.build())
      return
    }

    const isHorizontal = width >= height
    const startRelative = isHorizontal
      ? { x: -offset, y: 0 }
      : { x: 0, y: -offset }
    const endRelative = isHorizontal ? { x: offset, y: 0 } : { x: 0, y: offset }
    const startPoint = rotateAndTranslate(startRelative.x, startRelative.y)
    const endPoint = rotateAndTranslate(endRelative.x, endRelative.y)

    gb.add("flash_operation", {
      x: startPoint.x,
      y: mfy(startPoint.y),
    })
      .add("move_operation", {
        x: startPoint.x,
        y: mfy(startPoint.y),
      })
      .add("plot_operation", {
        x: endPoint.x,
        y: mfy(endPoint.y),
      })
      .add("flash_operation", {
        x: endPoint.x,
        y: mfy(endPoint.y),
      })

    glayer.push(...gb.build())
  }

  const getRegionApertureNumber = (glayer: AnyGerberCommand[]) =>
    findApertureNumber(glayer, REGION_APERTURE_CONFIG)

  const addClosedRegionFromPoints = ({
    target,
    apertureSource,
    points,
  }: {
    target: AnyGerberCommand[]
    apertureSource: AnyGerberCommand[]
    points: Array<{ x: number; y: number }>
  }) => {
    if (points.length === 0) return

    const regionApertureNumber = getRegionApertureNumber(apertureSource)
    const regionBuilder = gerberBuilder()
      .add("select_aperture", {
        aperture_number: regionApertureNumber,
      })
      .add("start_region_statement", {})

    regionBuilder.add("move_operation", {
      x: points[0].x,
      y: mfy(points[0].y),
    })

    for (let i = 1; i < points.length; i++) {
      regionBuilder.add("plot_operation", {
        x: points[i].x,
        y: mfy(points[i].y),
      })
    }

    regionBuilder.add("plot_operation", {
      x: points[0].x,
      y: mfy(points[0].y),
    })

    regionBuilder.add("end_region_statement", {})
    target.push(...regionBuilder.build())
  }

  const renderVectorText = (
    element: any,
    layer: LayerRef,
    layerType: "copper" | "silkscreen" | "fabrication",
    getApertureConfig: (elm: any) => any,
  ) => {
    if (element.layer !== layer) return
    // The acctual Uppercase letters are 70% of the font size
    // sources: https://forum.generic-mapping-tools.org/t/what-exactly-is-the-custom-font-s-height-point-size-ratio/1265/2
    const CAP_HEIGHT_SCALE = 0.7 // Adjust based on your font's metrics
    const glayer = glayers[getGerberLayerName(layer, layerType)]
    const apertureConfig = getApertureConfig(element)
    const gerber = gerberBuilder().add("select_aperture", {
      aperture_number: findApertureNumber(glayer, apertureConfig),
    })

    let initialX = element.anchor_position.x
    let initialY = element.anchor_position.y
    const fontSize = element.font_size * CAP_HEIGHT_SCALE
    const letterSpacing = fontSize * 0.4
    const spaceWidth = fontSize * 0.5

    const textWidth =
      element.text.split("").reduce((width: number, char: string) => {
        if (char === " ") {
          return width + spaceWidth + letterSpacing
        }
        return width + fontSize + letterSpacing
      }, 0) - letterSpacing

    const textHeight = fontSize

    const anchorAlignment =
      element.anchor_alignment ||
      (():
        | "top_center"
        | "bottom_center"
        | "center_left"
        | "center_right"
        | undefined => {
        const side = element.anchor_side as
          | "top"
          | "bottom"
          | "left"
          | "right"
          | undefined
        if (!side) return undefined
        switch (side) {
          case "top":
            return "top_center"
          case "bottom":
            return "bottom_center"
          case "left":
            return "center_left"
          case "right":
            return "center_right"
        }
      })() ||
      "center"

    switch (anchorAlignment) {
      case "top_left":
        break
      case "top_center":
        initialX -= textWidth / 2
        break
      case "top_right":
        initialX -= textWidth
        break
      case "center_right":
        initialY -= textHeight / 2
        break
      case "center_left":
        initialX -= textWidth
        initialY -= textHeight / 2
        break
      case "bottom_left":
        initialY -= textHeight
        break
      case "bottom_center":
        initialX -= textWidth / 2
        initialY -= textHeight
        break
      case "bottom_right":
        initialX -= textWidth
        initialY -= textHeight
        break
      default:
        initialX -= textWidth / 2
        initialY -= textHeight / 2
        break
    }

    let anchoredX = initialX
    const anchoredY = initialY

    let rotation = element.ccw_rotation || 0
    const cx = anchoredX + textWidth / 2
    const cy = anchoredY + textHeight / 2
    const transforms: Matrix[] = []

    const shouldMirror =
      element.is_mirrored !== undefined
        ? element.is_mirrored
        : element.layer === "bottom"

    if (shouldMirror) {
      transforms.push(
        translate(cx, cy),
        { a: -1, b: 0, c: 0, d: 1, e: 0, f: 0 },
        translate(-cx, -cy),
      )
      rotation = -rotation
    }

    if (rotation) {
      const rad = (rotation * Math.PI) / 180
      transforms.push(translate(cx, cy), rotate(rad), translate(-cx, -cy))
    }

    const transformMatrix =
      transforms.length > 0 ? compose(...transforms) : undefined

    const applyTransform = (point: { x: number; y: number }) =>
      transformMatrix ? applyToPoint(transformMatrix, point) : point

    if (element.is_knockout) {
      const padding = element.knockout_padding ?? {
        left: 0.2,
        right: 0.2,
        top: 0.2,
        bottom: 0.2,
      }

      const paddedRect = [
        { x: initialX - padding.left, y: initialY - padding.top },
        {
          x: initialX + textWidth + padding.right,
          y: initialY - padding.top,
        },
        {
          x: initialX + textWidth + padding.right,
          y: initialY + textHeight + padding.bottom,
        },
        {
          x: initialX - padding.left,
          y: initialY + textHeight + padding.bottom,
        },
      ].map(applyTransform)

      glayer.push(
        ...gerberBuilder()
          .add("select_aperture", {
            aperture_number: findApertureNumber(glayer, apertureConfig),
          })
          .add("start_region_statement", {})
          .add("move_operation", {
            x: paddedRect[0].x,
            y: mfy(paddedRect[0].y),
          })
          .add("plot_operation", {
            x: paddedRect[1].x,
            y: mfy(paddedRect[1].y),
          })
          .add("plot_operation", {
            x: paddedRect[2].x,
            y: mfy(paddedRect[2].y),
          })
          .add("plot_operation", {
            x: paddedRect[3].x,
            y: mfy(paddedRect[3].y),
          })
          .add("plot_operation", {
            x: paddedRect[0].x,
            y: mfy(paddedRect[0].y),
          })
          .add("end_region_statement", {})
          .build(),
      )

      glayer.push(
        ...gerberBuilder().add("set_layer_polarity", { polarity: "C" }).build(),
      )
    }

    for (const char of element.text) {
      if (char === " ") {
        anchoredX += spaceWidth + letterSpacing
        continue
      }

      const letterPaths = lineAlphabet[char] || []
      for (const path of letterPaths) {
        const x1 = anchoredX + path.x1 * fontSize
        const y1 = anchoredY + path.y1 * fontSize
        const x2 = anchoredX + path.x2 * fontSize
        const y2 = anchoredY + path.y2 * fontSize

        const p1 = applyTransform({ x: x1, y: y1 })
        const p2 = applyTransform({ x: x2, y: y2 })

        gerber.add("move_operation", { x: p1.x, y: mfy(p1.y) })
        gerber.add("plot_operation", { x: p2.x, y: mfy(p2.y) })
      }

      anchoredX += fontSize + letterSpacing
    }

    glayer.push(...gerber.build())

    if (element.is_knockout) {
      glayer.push(
        ...gerberBuilder().add("set_layer_polarity", { polarity: "D" }).build(),
      )
    }
  }

  // Helper to draw a ring into a gerber builder (used for copper pours)
  const drawRingToBuilder = (
    builder: ReturnType<typeof gerberBuilder>,
    ring: {
      vertices: Array<{ x: number; y: number; bulge?: number }>
    },
  ) => {
    if (!ring || ring.vertices.length === 0) return

    builder.add("move_operation", {
      x: ring.vertices[0].x,
      y: mfy(ring.vertices[0].y),
    })

    const vertices_loop = [...ring.vertices, ring.vertices[0]]

    for (let i = 0; i < vertices_loop.length - 1; i++) {
      const start = vertices_loop[i]
      const end = vertices_loop[i + 1]

      if (start.bulge && Math.abs(start.bulge) > 1e-9) {
        const bulge = (opts.flip_y_axis ? -1 : 1) * (start.bulge as number)
        if (bulge > 0) {
          // CCW
          builder.add("set_movement_mode_to_counterclockwise_circular", {})
        } else {
          // CW
          builder.add("set_movement_mode_to_clockwise_circular", {})
        }

        const p1 = { x: start.x, y: start.y }
        const p2 = { x: end.x, y: end.y }

        const dx = p2.x - p1.x
        const dy = p2.y - p1.y
        const d = Math.sqrt(dx * dx + dy * dy)

        if (d < 1e-9) {
          builder.add("set_movement_mode_to_linear", {})
          builder.add("plot_operation", {
            x: end.x,
            y: mfy(end.y),
          })
          continue
        }

        const abs_b = Math.abs(bulge)
        const theta = 4 * Math.atan(abs_b)
        if (Math.abs(Math.sin(theta / 2)) < 1e-9) {
          builder.add("set_movement_mode_to_linear", {})
          builder.add("plot_operation", {
            x: end.x,
            y: mfy(end.y),
          })
          continue
        }
        const R = d / (2 * Math.sin(theta / 2))
        const alpha = (Math.PI - theta) / 2
        const phi = Math.atan2(dy, dx)
        const delta_angle = bulge > 0 ? alpha : -alpha
        const angle_p1_center = phi + delta_angle
        const Cx = p1.x + R * Math.cos(angle_p1_center)
        const Cy = p1.y + R * Math.sin(angle_p1_center)

        const i_offset = Cx - p1.x
        const j_offset = Cy - p1.y

        builder.add("plot_operation", {
          x: end.x,
          y: mfy(end.y),
          i: i_offset,
          j: opts.flip_y_axis ? -j_offset : j_offset,
        })
      } else {
        // line
        builder.add("set_movement_mode_to_linear", {})
        builder.add("plot_operation", { x: end.x, y: mfy(end.y) })
      }
    }
  }

  // To conform to Gerber spec (CCW outer, CW inner) from tscircuit
  // spec (CW outer, CCW inner), we must reverse both.
  const reverseRing = (ring: {
    vertices: Array<{ x: number; y: number; bulge?: number }>
  }): {
    vertices: Array<{ x: number; y: number; bulge?: number }>
  } => {
    const { vertices } = ring
    if (!vertices || vertices.length === 0) return { vertices: [] }
    const n = vertices.length

    const reversed_pts = [...vertices].reverse()

    const new_vertices = reversed_pts.map((pt, i) => {
      const bulge_v_idx = (n - 2 - i + n) % n
      const bulge = vertices[bulge_v_idx].bulge
      return { ...pt, bulge: bulge ? -bulge : undefined }
    })

    return { vertices: new_vertices }
  }

  // FIRST PASS: Process copper pours first
  // Copper pours must be rendered before traces/pads/vias so that:
  // 1. Pour is drawn (LPD - dark)
  // 2. Cutouts are drawn (LPC - clear)
  // 3. Traces/pads/vias are drawn on top (LPD - dark)
  for (const layer of copperLayerRefs) {
    for (const element of circuitJson) {
      if (element.type === "pcb_copper_pour" && layer === element.layer) {
        const copper_glayer = glayers[getGerberLayerName(layer, "copper")]

        // Collect all pour commands for this element
        const all_pour_commands: AnyGerberCommand[] = []

        if (element.shape === "brep") {
          const { brep_shape } = element
          if (!brep_shape) continue
          const { outer_ring, inner_rings } = brep_shape
          const regionApertureNumber = getRegionApertureNumber(copper_glayer)

          // Draw outer ring as a filled region (dark polarity - default)
          const outer_builder = gerberBuilder()
            .add("select_aperture", { aperture_number: regionApertureNumber })
            .add("start_region_statement", {})

          drawRingToBuilder(outer_builder, reverseRing(outer_ring))

          outer_builder.add("end_region_statement", {})
          all_pour_commands.push(...outer_builder.build())

          // Draw inner rings (cutouts) using clear polarity
          // Each inner ring is drawn as a separate region with LPC (clear)
          if (inner_rings && inner_rings.length > 0) {
            // Switch to clear polarity
            all_pour_commands.push(
              ...gerberBuilder()
                .add("set_layer_polarity", { polarity: "C" })
                .build(),
            )

            for (const inner_ring of inner_rings) {
              const inner_builder = gerberBuilder()
                .add("select_aperture", {
                  aperture_number: regionApertureNumber,
                })
                .add("start_region_statement", {})

              // For clear polarity, we don't need to reverse - draw as-is
              // The polarity handles the "hole" semantics
              drawRingToBuilder(inner_builder, inner_ring)

              inner_builder.add("end_region_statement", {})
              all_pour_commands.push(...inner_builder.build())
            }

            // Switch back to dark polarity
            all_pour_commands.push(
              ...gerberBuilder()
                .add("set_layer_polarity", { polarity: "D" })
                .build(),
            )
          }
        } else if (element.shape === "rect") {
          const { center, width, height, rotation } = element
          const w = (width as number) / 2
          const h = (height as number) / 2

          const points = [
            { x: -w, y: h }, // Top-left
            { x: w, y: h }, // Top-right
            { x: w, y: -h }, // Bottom-right
            { x: -w, y: -h }, // Bottom-left
          ]

          let transformMatrix = identity()
          if (rotation) {
            const angle_rad = ((rotation as number) * Math.PI) / 180
            transformMatrix = rotate(angle_rad)
          }
          transformMatrix = compose(
            translate(center.x, center.y),
            transformMatrix,
          )

          const transformedPoints = points.map((p) =>
            applyToPoint(transformMatrix, p),
          )

          const regionApertureNumber = getRegionApertureNumber(copper_glayer)
          const rect_builder = gerberBuilder()
            .add("select_aperture", { aperture_number: regionApertureNumber })
            .add("start_region_statement", {})

          rect_builder.add("move_operation", {
            x: transformedPoints[0].x,
            y: mfy(transformedPoints[0].y),
          })
          for (let i = 1; i < transformedPoints.length; i++) {
            rect_builder.add("plot_operation", {
              x: transformedPoints[i].x,
              y: mfy(transformedPoints[i].y),
            })
          }
          rect_builder.add("plot_operation", {
            x: transformedPoints[0].x,
            y: mfy(transformedPoints[0].y),
          })

          rect_builder.add("end_region_statement", {})
          all_pour_commands.push(...rect_builder.build())
        } else if (element.shape === "polygon") {
          const { points } = element
          addClosedRegionFromPoints({
            target: all_pour_commands,
            apertureSource: copper_glayer,
            points,
          })
        }

        copper_glayer.push(...all_pour_commands)

        if (element.covered_with_solder_mask === false) {
          const mask_layer = glayers[getGerberLayerName(layer, "soldermask")]
          mask_layer.push(...all_pour_commands)
        }
      }
    }
  }

  // SECOND PASS: Process all other elements (traces, pads, vias, etc.)
  for (const layer of [...copperLayerRefs, "edgecut"] as const) {
    for (const element of circuitJson) {
      if (element.type === "pcb_trace") {
        if (layer === "edgecut") continue
        const route = element.route
        for (const [a, b] of pairs(route)) {
          // TODO b kind of matters here, this doesn't handle a bunch of cases
          // but the definition of a route is also kind of broken, a "wire" is
          // a relationship between two points and can't really be a type of
          // point
          if (a.route_type === "wire") {
            if (a.layer === layer) {
              let bPoint: { x: number; y: number } | null = null
              if (b.route_type === "wire" || b.route_type === "via") {
                bPoint = b
              } else if (b.route_type === "through_pad") {
                if (b.start_layer === layer) {
                  bPoint = b.start
                } else if (b.end_layer === layer) {
                  bPoint = b.end
                }
              }
              if (!bPoint) continue

              const glayer = glayers[getGerberLayerName(layer, "copper")]
              glayer.push(
                ...gerberBuilder()
                  .add("select_aperture", {
                    aperture_number: findApertureNumber(glayer, {
                      trace_width: a.width,
                    }),
                  })
                  .add("move_operation", { x: a.x, y: mfy(a.y) })
                  .add("plot_operation", { x: bPoint.x, y: mfy(bPoint.y) })
                  .build(),
              )
            }
          } else if (a.route_type === "via" && b.route_type === "wire") {
            if (b.layer === layer) {
              const glayer = glayers[getGerberLayerName(layer, "copper")]
              glayer.push(
                ...gerberBuilder()
                  .add("select_aperture", {
                    aperture_number: findApertureNumber(glayer, {
                      trace_width: b.width,
                    }),
                  })
                  .add("move_operation", { x: a.x, y: mfy(a.y) })
                  .add("plot_operation", { x: b.x, y: mfy(b.y) })
                  .build(),
              )
            }
          } else if (
            a.route_type === "through_pad" &&
            b.route_type === "wire"
          ) {
            if (b.layer === layer) {
              const aPoint =
                a.end_layer === layer
                  ? a.end
                  : a.start_layer === layer
                    ? a.start
                    : null
              if (!aPoint) continue

              const glayer = glayers[getGerberLayerName(layer, "copper")]
              glayer.push(
                ...gerberBuilder()
                  .add("select_aperture", {
                    aperture_number: findApertureNumber(glayer, {
                      trace_width: b.width,
                    }),
                  })
                  .add("move_operation", { x: aPoint.x, y: mfy(aPoint.y) })
                  .add("plot_operation", { x: b.x, y: mfy(b.y) })
                  .build(),
              )
            }
          }
        }
        for (const point of route) {
          if (
            point.route_type === "via" &&
            typeof point.outer_diameter === "number" &&
            (point.from_layer === layer || point.to_layer === layer)
          ) {
            const glayer = glayers[getGerberLayerName(layer, "copper")]
            glayer.push(
              ...gerberBuilder()
                .add("select_aperture", {
                  aperture_number: findApertureNumber(
                    glayer,
                    getApertureConfigFromOuterDiameter(point),
                  ),
                })
                .add("flash_operation", { x: point.x, y: mfy(point.y) })
                .build(),
            )
          }
        }
      } else if (
        element.type === "pcb_silkscreen_path" &&
        isOuterLayerRef(layer)
      ) {
        if (element.layer === layer) {
          const glayer = glayers[getGerberLayerName(layer, "silkscreen")]
          const apertureConfig = getApertureConfigFromPcbSilkscreenPath(element)
          const gerber = gerberBuilder().add("select_aperture", {
            aperture_number: findApertureNumber(glayer, apertureConfig),
          })
          // Move to the first point
          if (element.route.length > 0) {
            gerber.add("move_operation", {
              x: element.route[0].x,
              y: mfy(element.route[0].y),
            })
          }

          // Plot lines to subsequent points
          for (let i = 1; i < element.route.length; i++) {
            gerber.add("plot_operation", {
              x: element.route[i].x,
              y: mfy(element.route[i].y),
            })
          }

          glayer.push(...gerber.build())
        }
      } else if (isSilkscreenShape(element) && isOuterLayerRef(layer)) {
        if (element.layer === layer) {
          const stroke = getSilkscreenShapeStroke(element)
          if (stroke) {
            const glayer = glayers[getGerberLayerName(layer, "silkscreen")]
            // Mirror the fabrication rect handling: fill when is_filled, and
            // stroke unless has_stroke is explicitly false.
            const isFilled = (element as any).is_filled === true
            const isStroked = (element as any).has_stroke !== false

            if (isFilled) {
              addClosedRegionFromPoints({
                target: glayer,
                apertureSource: glayer,
                points: stroke.route,
              })
            }
            if (isStroked) {
              renderOpenPath({
                element: {
                  type: element.type,
                  stroke_width: stroke.strokeWidth,
                  is_stroke_dashed: (element as any).is_stroke_dashed,
                },
                glayer,
                apertureConfig: {
                  standard_template_code: "C",
                  diameter: stroke.strokeWidth,
                },
                route: stroke.route,
                mapY: mfy,
              })
            }
          }
        }
      } else if (
        // pcb_silkscreen_graphic is newer than this repo's pinned circuit-json,
        // so it isn't in the AnyCircuitElement union yet — match it structurally.
        (element as { type: string }).type === "pcb_silkscreen_graphic" &&
        isOuterLayerRef(layer)
      ) {
        type Ring = {
          vertices: Array<{ x: number; y: number; bulge?: number }>
        }
        const graphic = element as unknown as {
          layer: LayerRef
          brep_shape?: { outer_ring: Ring; inner_rings?: Ring[] }
        }
        if (graphic.layer === layer && graphic.brep_shape) {
          const glayer = glayers[getGerberLayerName(layer, "silkscreen")]
          const { outer_ring, inner_rings } = graphic.brep_shape
          const regionApertureNumber = getRegionApertureNumber(glayer)

          // Outer ring: a filled region (dark polarity).
          const outerBuilder = gerberBuilder()
            .add("select_aperture", { aperture_number: regionApertureNumber })
            .add("start_region_statement", {})
          drawRingToBuilder(outerBuilder, reverseRing(outer_ring))
          outerBuilder.add("end_region_statement", {})
          glayer.push(...outerBuilder.build())

          // Inner rings: holes, cut out with clear polarity, then restore dark.
          if (inner_rings && inner_rings.length > 0) {
            glayer.push(
              ...gerberBuilder()
                .add("set_layer_polarity", { polarity: "C" })
                .build(),
            )
            for (const inner_ring of inner_rings) {
              const innerBuilder = gerberBuilder()
                .add("select_aperture", {
                  aperture_number: regionApertureNumber,
                })
                .add("start_region_statement", {})
              drawRingToBuilder(innerBuilder, inner_ring)
              innerBuilder.add("end_region_statement", {})
              glayer.push(...innerBuilder.build())
            }
            glayer.push(
              ...gerberBuilder()
                .add("set_layer_polarity", { polarity: "D" })
                .build(),
            )
          }
        }
      } else if (
        element.type === "pcb_silkscreen_text" &&
        isOuterLayerRef(layer)
      ) {
        renderVectorText(
          element,
          layer as LayerRef,
          "silkscreen",
          getApertureConfigFromPcbSilkscreenText,
        )
      } else if (
        element.type === "pcb_fabrication_note_path" &&
        isOuterLayerRef(layer)
      ) {
        if (element.layer === layer) {
          renderOpenPath({
            element,
            glayer:
              glayers[getGerberLayerName(layer as LayerRef, "fabrication")],
            apertureConfig:
              getApertureConfigFromPcbFabricationNotePath(element),
            route: element.route,
            mapY: mfy,
          })
        }
      } else if (
        element.type === "pcb_fabrication_note_rect" &&
        isOuterLayerRef(layer)
      ) {
        if (element.layer === layer) {
          const glayer =
            glayers[getGerberLayerName(layer as LayerRef, "fabrication")]
          const points = getFabRectPoints(element)

          if (element.is_filled === true) {
            addClosedRegionFromPoints({
              target: glayer,
              apertureSource: glayer,
              points,
            })
          }

          if (element.has_stroke !== false) {
            renderOpenPath({
              element,
              glayer,
              apertureConfig:
                getApertureConfigFromPcbFabricationNotePath(element),
              route: [...points, points[0]],
              mapY: mfy,
            })
          }
        }
      } else if (
        element.type === "pcb_fabrication_note_dimension" &&
        isOuterLayerRef(layer)
      ) {
        if (element.layer === layer) {
          renderFabricationDimension({
            element,
            glayer:
              glayers[getGerberLayerName(layer as LayerRef, "fabrication")],
            mapY: mfy,
            renderText: (textElement) =>
              renderVectorText(
                textElement,
                layer as LayerRef,
                "fabrication",
                getApertureConfigFromPcbFabricationNoteText,
              ),
          })
        }
      } else if (
        element.type === "pcb_fabrication_note_text" &&
        isOuterLayerRef(layer)
      ) {
        renderVectorText(
          element,
          layer as LayerRef,
          "fabrication",
          getApertureConfigFromPcbFabricationNoteText,
        )
      } else if (element.type === "pcb_copper_text" && layer !== "edgecut") {
        renderVectorText(
          element,
          layer,
          "copper",
          getApertureConfigFromPcbCopperText,
        )
      } else if (element.type === "pcb_smtpad" && element.shape !== "polygon") {
        if (element.layer === layer && isOuterLayerRef(layer)) {
          if (element.shape === "pill" || element.shape === "rotated_pill") {
            const soldermaskMargin =
              typeof element.soldermask_margin === "number"
                ? element.soldermask_margin
                : 0
            const rotation =
              element.shape === "rotated_pill" &&
              typeof element.ccw_rotation === "number"
                ? element.ccw_rotation
                : 0

            renderPillFlash({
              glayer: glayers[getGerberLayerName(layer, "copper")],
              x: element.x,
              y: element.y,
              width: element.width,
              height: element.height,
              rotationDegrees: rotation,
            })

            renderPillFlash({
              glayer: glayers[getGerberLayerName(layer, "soldermask")],
              x: element.x,
              y: element.y,
              width: element.width + soldermaskMargin * 2,
              height: element.height + soldermaskMargin * 2,
              rotationDegrees: rotation,
            })

            continue
          }

          const rotation =
            element.shape === "rotated_rect" &&
            typeof element.ccw_rotation === "number"
              ? element.ccw_rotation
              : 0

          for (const { glayer, apertureConfig } of [
            {
              glayer: glayers[getGerberLayerName(layer, "copper")],
              apertureConfig: getApertureConfigFromPcbSmtpad(element),
            },
            {
              glayer: glayers[getGerberLayerName(layer, "soldermask")],
              apertureConfig: getApertureConfigFromPcbSmtpadSoldermask(element),
            },
          ]) {
            const apertureNumber = findApertureNumber(glayer, apertureConfig)
            const gb = gerberBuilder().add("select_aperture", {
              aperture_number: apertureNumber,
            })

            if (rotation) {
              gb.add("load_rotation", {
                rotation_degrees: rotation,
              })
            }

            gb.add("flash_operation", { x: element.x, y: mfy(element.y) })

            if (rotation) {
              // Reset rotation
              gb.add("load_rotation", { rotation_degrees: 0 })
            }

            glayer.push(...gb.build())
          }
        }
      } else if (element.type === "pcb_smtpad" && element.shape === "polygon") {
        if (element.layer === layer && isOuterLayerRef(layer)) {
          const layers_to_add_to = [
            glayers[getGerberLayerName(layer, "copper")],
          ]

          if (element.is_covered_with_solder_mask !== true) {
            layers_to_add_to.push(
              glayers[getGerberLayerName(layer, "soldermask")],
            )
          }

          for (const glayer of layers_to_add_to) {
            const { points } = element
            if (!points) continue
            addClosedRegionFromPoints({
              target: glayer,
              apertureSource: glayer,
              points,
            })
          }
        }
      } else if (element.type === "pcb_solder_paste") {
        if (element.layer === layer && isOuterLayerRef(layer)) {
          const glayer = glayers[getGerberLayerName(layer, "paste")]
          let rotation = 0
          if (
            "ccw_rotation" in element &&
            typeof element.ccw_rotation === "number"
          ) {
            rotation = element.ccw_rotation
          } else {
            // Solder paste has no rotation, so use the matching plated hole's rotation.
            const platedHole = circuitJson.find(
              (
                candidate,
              ): candidate is PcbPlatedHole & { ccw_rotation: number } =>
                candidate.type === "pcb_plated_hole" &&
                candidate.x === element.x &&
                candidate.y === element.y &&
                "ccw_rotation" in candidate &&
                typeof candidate.ccw_rotation === "number",
            )
            if (platedHole) {
              rotation = platedHole.ccw_rotation
            }
          }

          let apertureConfig = getApertureConfigFromPcbSolderPaste(element)
          if (
            element.shape === "pill" &&
            (rotation === 90 || rotation === 270)
          ) {
            apertureConfig = getApertureConfigFromPcbSolderPaste({
              ...element,
              width: element.height,
              height: element.width,
            })
            rotation = 0
          }

          const aperture_number = findApertureNumber(glayer, apertureConfig)

          const gb = gerberBuilder().add("select_aperture", {
            aperture_number,
          })
          if (rotation) {
            gb.add("load_rotation", {
              rotation_degrees: rotation,
            })
          }
          gb.add("flash_operation", { x: element.x, y: mfy(element.y) })
          if (rotation) {
            gb.add("load_rotation", { rotation_degrees: 0 })
          }
          glayer.push(...gb.build())
        }
      } else if (element.type === "pcb_plated_hole") {
        if (element.layers.includes(layer as any)) {
          const layersToAddTo = [
            glayers[getGerberLayerName(layer, "copper")],
          ] as AnyGerberCommand[][]
          if (
            isOuterLayerRef(layer) &&
            element.is_covered_with_solder_mask !== true
          ) {
            layersToAddTo.push(glayers[getGerberLayerName(layer, "soldermask")])
          }
          for (const glayer of layersToAddTo) {
            if (element.shape === "hole_with_polygon_pad") {
              const { pad_outline } = element
              if (!pad_outline?.length) continue
              const soldermaskGlayer =
                glayers[getGerberLayerName(layer, "soldermask")]
              let points = pad_outline
              if (
                glayer === soldermaskGlayer &&
                "soldermask_margin" in element &&
                typeof element.soldermask_margin === "number"
              ) {
                points = offsetPolygonOutline(
                  pad_outline,
                  element.soldermask_margin,
                )
              }
              const translatedPoints = points.map((point) => ({
                x: point.x + element.x,
                y: point.y + element.y,
              }))
              addClosedRegionFromPoints({
                target: glayer,
                apertureSource: glayer,
                points: translatedPoints,
              })
              continue
            }

            // --- Compute effective copper pad dimensions ---
            const holeW =
              "hole_width" in element && typeof element.hole_width === "number"
                ? element.hole_width
                : 0
            const holeH =
              "hole_height" in element &&
              typeof element.hole_height === "number"
                ? element.hole_height
                : 0

            const padWidthCandidates: number[] = [holeW]
            if (
              "outer_width" in element &&
              typeof element.outer_width === "number"
            ) {
              padWidthCandidates.push(element.outer_width)
            }
            if (
              "rect_pad_width" in element &&
              typeof element.rect_pad_width === "number"
            ) {
              padWidthCandidates.push(element.rect_pad_width)
            }
            const padW = Math.max(...padWidthCandidates)

            const padHeightCandidates: number[] = [holeH]
            if (
              "outer_height" in element &&
              typeof element.outer_height === "number"
            ) {
              padHeightCandidates.push(element.outer_height)
            }
            if (
              "rect_pad_height" in element &&
              typeof element.rect_pad_height === "number"
            ) {
              padHeightCandidates.push(element.rect_pad_height)
            }
            const padH = Math.max(...padHeightCandidates)
            const soldermaskGlayer =
              glayers[getGerberLayerName(layer, "soldermask")]
            let soldermaskMargin = 0
            if (
              glayer === soldermaskGlayer &&
              "soldermask_margin" in element &&
              typeof element.soldermask_margin === "number"
            ) {
              soldermaskMargin = element.soldermask_margin
            }
            const aperturePadW = padW + soldermaskMargin * 2
            const aperturePadH = padH + soldermaskMargin * 2

            if (element.shape === "pill") {
              // Use min dimension as slot width (aperture circle)
              const circleApertureConfig = {
                standard_template_code: "C" as const,
                diameter: Math.min(aperturePadW, aperturePadH),
              }

              let aperture_number: number
              try {
                aperture_number = findApertureNumber(
                  glayer,
                  circleApertureConfig,
                )
              } catch {
                aperture_number =
                  Math.max(
                    ...glayer
                      .filter((cmd) => "aperture_number" in cmd)
                      .map((cmd) => cmd.aperture_number),
                    9,
                  ) + 1

                glayer.push(
                  ...gerberBuilder()
                    .add("define_aperture_template", {
                      aperture_number,
                      ...circleApertureConfig,
                    })
                    .build(),
                )
              }

              const gb = gerberBuilder().add("select_aperture", {
                aperture_number,
              })

              const rotationRadians =
                ((element.ccw_rotation ?? 0) * Math.PI) / 180
              const cosTheta = Math.cos(rotationRadians)
              const sinTheta = Math.sin(rotationRadians)

              const rotateAndTranslate = (dx: number, dy: number) => {
                const rotatedX = dx * cosTheta - dy * sinTheta
                const rotatedY = dx * sinTheta + dy * cosTheta
                return {
                  x: element.x + rotatedX,
                  y: element.y + rotatedY,
                }
              }

              const isHorizontal = aperturePadW >= aperturePadH
              const offset = isHorizontal
                ? (aperturePadW - aperturePadH) / 2
                : (aperturePadH - aperturePadW) / 2

              if (offset <= 0) {
                const center = rotateAndTranslate(0, 0)
                gb.add("flash_operation", { x: center.x, y: mfy(center.y) })
              } else {
                const startRelative = isHorizontal
                  ? { x: -offset, y: 0 }
                  : { x: 0, y: -offset }
                const endRelative = isHorizontal
                  ? { x: offset, y: 0 }
                  : { x: 0, y: offset }

                const startPoint = rotateAndTranslate(
                  startRelative.x,
                  startRelative.y,
                )
                const endPoint = rotateAndTranslate(
                  endRelative.x,
                  endRelative.y,
                )

                gb.add("flash_operation", {
                  x: startPoint.x,
                  y: mfy(startPoint.y),
                })
                  .add("move_operation", {
                    x: startPoint.x,
                    y: mfy(startPoint.y),
                  })
                  .add("plot_operation", {
                    x: endPoint.x,
                    y: mfy(endPoint.y),
                  })
                  .add("flash_operation", {
                    x: endPoint.x,
                    y: mfy(endPoint.y),
                  })
              }

              glayer.push(...gb.build())
            } else {
              // Non-pill shapes (rect, circle)
              let apertureConfig = getApertureConfigFromPcbPlatedHole({
                ...element,
                ...(element.shape !== "circle"
                  ? { outer_width: padW, outer_height: padH }
                  : {}),
              })
              if (glayer === soldermaskGlayer) {
                apertureConfig = getApertureConfigFromPcbPlatedHoleSoldermask({
                  ...element,
                  ...(element.shape !== "circle"
                    ? { outer_width: padW, outer_height: padH }
                    : {}),
                })
              }
              const rotation =
                "rect_ccw_rotation" in element &&
                typeof element.rect_ccw_rotation === "number" &&
                Math.abs(padW - padH) > 1e-9
                  ? element.rect_ccw_rotation
                  : undefined

              const gb = gerberBuilder().add("select_aperture", {
                aperture_number: findApertureNumber(glayer, apertureConfig),
              })

              if (rotation)
                gb.add("load_rotation", { rotation_degrees: rotation })

              gb.add("flash_operation", { x: element.x, y: mfy(element.y) })

              if (rotation) gb.add("load_rotation", { rotation_degrees: 0 })

              glayer.push(...gb.build())
            }
          }
        }
      } else if (element.type === "pcb_hole") {
        if (
          isOuterLayerRef(layer) &&
          element.is_covered_with_solder_mask !== true
        ) {
          for (const glayer of [
            glayers[getGerberLayerName(layer, "soldermask")],
          ]) {
            if (element.hole_shape !== "circle") {
              console.warn(
                "NOT IMPLEMENTED: drawing gerber for non-round holes",
              )
              continue
            }
            glayer.push(
              ...gerberBuilder()
                .add("select_aperture", {
                  aperture_number: findApertureNumber(
                    glayer,
                    getApertureConfigFromCirclePcbHoleSoldermask(element),
                  ),
                })
                .add("flash_operation", { x: element.x, y: mfy(element.y) })
                .build(),
            )
          }
        }
      } else if (element.type === "pcb_via") {
        if (element.layers.includes(layer as any)) {
          const layersToAddTo = [
            glayers[getGerberLayerName(layer, "copper")],
          ] as AnyGerberCommand[][]
          if (element.is_tented === false && isOuterLayerRef(layer)) {
            layersToAddTo.push(glayers[getGerberLayerName(layer, "soldermask")])
          }
          for (const glayer of layersToAddTo) {
            glayer.push(
              ...gerberBuilder()
                .add("select_aperture", {
                  aperture_number: findApertureNumber(
                    glayer,
                    getApertureConfigFromOuterDiameter(element),
                  ),
                })
                .add("flash_operation", { x: element.x, y: mfy(element.y) })
                .build(),
            )
          }
        }
      } else if (element.type === "pcb_board" && layer === "edgecut") {
        // Skip boards when a panel exists
        if (hasPanel) continue

        const glayer = glayers.Edge_Cuts
        const { width, height, center, outline } = element
        const gerberBuild = gerberBuilder().add("select_aperture", {
          aperture_number: 10,
        })

        if (outline && outline.length > 2) {
          const boardPolygon: Polygon = [
            outline.map((point) => [point.x, point.y]),
          ]
          let boardGeometry: MultiPolygon = [boardPolygon]
          if (solidCutoutPolygons.length > 0) {
            boardGeometry = polygonClipping.difference(
              boardPolygon,
              ...solidCutoutPolygons,
            )
          }

          for (const polygon of boardGeometry) {
            for (const ring of polygon) {
              emitEdgeCutRing(gerberBuild, ring, mfy)
            }
          }
        } else {
          const boardPolygon: Polygon = [
            [
              [center!.x - width! / 2, center!.y - height! / 2],
              [center!.x + width! / 2, center!.y - height! / 2],
              [center!.x + width! / 2, center!.y + height! / 2],
              [center!.x - width! / 2, center!.y + height! / 2],
            ],
          ]
          let boardGeometry: MultiPolygon = [boardPolygon]
          if (solidCutoutPolygons.length > 0) {
            boardGeometry = polygonClipping.difference(
              boardPolygon,
              ...solidCutoutPolygons,
            )
          }

          for (const polygon of boardGeometry) {
            for (const ring of polygon) {
              emitEdgeCutRing(gerberBuild, ring, mfy)
            }
          }
        }

        glayer.push(...gerberBuild.build())
      } else if (element.type === "pcb_panel" && layer === "edgecut") {
        const glayer = glayers.Edge_Cuts
        const panel = element
        const { width, height, center } = panel
        const gerberBuild = gerberBuilder()
          .add("select_aperture", {
            aperture_number: 10,
          })
          .add("move_operation", {
            x: center!.x - width! / 2,
            y: mfy(center!.y - height! / 2),
          })
          .add("plot_operation", {
            x: center!.x + width! / 2,
            y: mfy(center!.y - height! / 2),
          })
          .add("plot_operation", {
            x: center!.x + width! / 2,
            y: mfy(center!.y + height! / 2),
          })
          .add("plot_operation", {
            x: center!.x - width! / 2,
            y: mfy(center!.y + height! / 2),
          })
          .add("plot_operation", {
            x: center!.x - width! / 2,
            y: mfy(center!.y - height! / 2),
          })

        glayer.push(...gerberBuild.build())
      } else if (element.type === "pcb_cutout") {
        if (layer === "edgecut") {
          // Skip cutouts that overlap the board edge — they are already merged
          // into the board outline polygon above and must not be double-emitted.
          if (
            hasBoard &&
            !hasPanel &&
            doesSolidCutoutOverlapBoardEdge({
              cutout: element as PcbCutout,
              boardOutlinePolygons,
            })
          ) {
            continue
          }
          const ec_layer = glayers.Edge_Cuts
          const cutout_builder = gerberBuilder().add("select_aperture", {
            aperture_number: 10,
          })
          // Internal cutouts (fully inside the board, not touching the edge)
          // must use CW winding so fill rules treat the loop as a hole.
          const drawCw =
            hasBoard &&
            !hasPanel &&
            isCutoutFullyInternal({
              cutout: element as PcbCutout,
              boardOutlinePolygons,
            })
          emitCutoutEdgeCuts({
            cutout: element as PcbCutout,
            builder: cutout_builder,
            mfy,
            drawCw,
          })
          ec_layer.push(...cutout_builder.build())
        }
      }
    }
  }

  for (const key of Object.keys(glayers)) {
    glayers[key as keyof LayerToGerberCommandsMap].push(
      ...gerberBuilder().add("end_of_file", {}).build(),
    )
  }

  return glayers
}
